import { useEffect, useMemo, useState } from 'react';
import useBreakpoints from '../../../common/hooks/useBreakpoints';
import Loader from '../../../common/widgets/Loader';
import LoadingSpinner from '../../../common/widgets/LoadingSpinner';

import {
  flattenAutocompleteSuggestions,
  getAutocompleteSuggestionTitle,
} from '../helpers/autocompleteHelper';
import {
  AutocompleteResponseModel,
  AutocompleteSuggestionItem,
} from '../models/Search';
import SearchSuggestionItem from './SearchSuggestionItem';

interface ISugestionsPorps {
  suggestions: AutocompleteResponseModel | null;
  searchValue: string;
  isLoading: boolean;
  sugestionsRef: any;
  onClick: (sugestion: AutocompleteSuggestionItem, index: number) => void;
}

const removeDiacritics = (text: string): string =>
  text
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const escapeRegExp = (text: string): string => {
  return text.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
};

const SearchSugestions = (props: ISugestionsPorps) => {
  const { suggestions, searchValue, isLoading, sugestionsRef, onClick } = props;

  const { isTablet } = useBreakpoints();

  const flattenedSuggestions = useMemo(
    () => flattenAutocompleteSuggestions(suggestions),
    [suggestions],
  );

  const [focusedIndex, setFocusedIndex] = useState(-1);

  const suggestionsClassName = isTablet
    ? 'search-suggestions-mobile'
    : 'search-suggestions';

  const normalizedSearch = removeDiacritics(searchValue);

  const getHighlightedTitle = (title: string): string => {
    if (!normalizedSearch) {
      return title;
    }

    const normalizedTitle = removeDiacritics(title);
    const regex = new RegExp(escapeRegExp(normalizedSearch), 'gi');

    const parts: string[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(normalizedTitle)) !== null) {
      const start = match.index;
      const end = start + match[0].length;

      if (start > lastIndex) {
        parts.push(title.slice(lastIndex, start));
      }

      parts.push(`<strong>${title.slice(start, end)}</strong>`);

      lastIndex = end;
    }

    if (lastIndex < title.length) {
      parts.push(title.slice(lastIndex));
    }

    return parts.join('');
  };

  const handleKeyDown = (e: any) => {
    if (flattenedSuggestions.length === 0) {
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();

      setFocusedIndex(prevIndex =>
        prevIndex < flattenedSuggestions.length - 1 ? prevIndex + 1 : prevIndex,
      );

      if (focusedIndex > 3 && (focusedIndex & 3) === 0) {
        sugestionsRef?.current.scrollBy({ top: 160, behaviour: 'smooth' });
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();

      setFocusedIndex(prevIndex => (prevIndex > 0 ? prevIndex - 1 : -1));

      if (
        focusedIndex < flattenedSuggestions.length - 4 &&
        (focusedIndex & 3) === 0
      ) {
        sugestionsRef?.current.scrollBy({ top: -160, behaviour: 'smooth' });
      }
    } else if (e.key === 'Enter' && focusedIndex !== -1) {
      onClick(flattenedSuggestions[focusedIndex], focusedIndex + 1);
    }
  };

  useEffect(() => {
    setFocusedIndex(-1);
  }, [suggestions]);

  if (flattenedSuggestions.length === 0 && !isLoading) {
    return null;
  }

  return (
    <div className={suggestionsClassName}>
      <ul
        className={isTablet ? 'no-results-sugestions-list' : ''}
        ref={sugestionsRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <Loader loading={isLoading} loader={<LoadingSpinner size="small" />}>
          {flattenedSuggestions.map((suggestion, index) => {
            const title = getAutocompleteSuggestionTitle(suggestion);

            return (
              <SearchSuggestionItem
                key={`${suggestion.type}-${title}-${index}`}
                suggestion={suggestion}
                index={index}
                focused={focusedIndex === index}
                highlightedTitle={getHighlightedTitle(title)}
                onClick={onClick}
              />
            );
          })}
        </Loader>
      </ul>
    </div>
  );
};

export default SearchSugestions;
